import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, FileText, Briefcase, TrendingUp, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const API = '/api';

function getAuthHeaders() {
  const token = localStorage.getItem('pkepl_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <p className="text-2xl font-bold text-[#153E5C] font-serif mb-1">{value ?? '—'}</p>
      <p className="text-sm font-medium text-[#374151] font-sans">{label}</p>
      {sub && <p className="text-xs text-[#6B7280] font-sans mt-1">{sub}</p>}
    </div>
  );
}

function statusBadge(status) {
  const map = {
    new: 'bg-blue-50 text-blue-700 border-blue-200',
    read: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    replied: 'bg-green-50 text-green-700 border-green-200',
    archived: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { withCredentials: true, headers: getAuthHeaders() })
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white border border-[#E5E7EB] rounded-xl p-6 animate-pulse">
              <div className="h-10 w-10 bg-[#F3F4F6] rounded-lg mb-4" />
              <div className="h-7 w-16 bg-[#F3F4F6] rounded mb-2" />
              <div className="h-4 w-24 bg-[#F3F4F6] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-serif text-2xl font-bold text-[#153E5C] mb-1">Dashboard</h1>
        <p className="text-sm text-[#6B7280] font-sans">Overview of your website content and inquiries.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={MessageSquare} label="Total Inquiries" value={stats?.total_inquiries} sub={`${stats?.new_inquiries || 0} new`} color="bg-[#0E5A57]" />
        <StatCard icon={AlertCircle} label="New Inquiries" value={stats?.new_inquiries} sub="Awaiting response" color="bg-blue-500" />
        <StatCard icon={FileText} label="Blog Posts" value={stats?.total_posts} sub={`${stats?.published_posts || 0} published`} color="bg-[#153E5C]" />
        <StatCard icon={CheckCircle} label="Published Posts" value={stats?.published_posts} sub="Live on site" color="bg-green-600" />
        <StatCard icon={Briefcase} label="Case Studies" value={stats?.total_cases} sub={`${stats?.published_cases || 0} published`} color="bg-[#D4AF37]" />
        <StatCard icon={TrendingUp} label="Published Cases" value={stats?.published_cases} sub="Live on site" color="bg-purple-600" />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'View Inquiries', href: '/admin/inquiries', desc: 'Manage incoming inquiries' },
          { label: 'Edit Blog Posts', href: '/admin/blog', desc: 'Create and manage insights' },
          { label: 'Manage Cases', href: '/admin/cases', desc: 'Update case experiences' },
          { label: 'Update SEO', href: '/admin/seo', desc: 'Manage metadata per page' },
        ].map(item => (
          <Link key={item.href} to={item.href}
            className="group p-5 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#0E5A57]/30 hover:shadow-sm transition-all duration-200">
            <p className="font-medium text-sm text-[#1F2937] group-hover:text-[#0E5A57] transition-colors duration-200 font-sans mb-1">{item.label}</p>
            <p className="text-xs text-[#9CA3AF] font-sans">{item.desc}</p>
          </Link>
        ))}
      </div>

      {/* Recent Inquiries */}
      {stats?.recent_inquiries?.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#F3F4F6] flex items-center justify-between">
            <h2 className="font-semibold text-sm text-[#374151] font-sans">Recent Inquiries</h2>
            <Link to="/admin/inquiries" className="text-xs text-[#0E5A57] hover:text-[#2E7D7A] font-medium transition-colors duration-150 font-sans">
              View all →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full admin-table">
              <thead className="bg-[#F9FAFB]">
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-left hidden sm:table-cell">Company</th>
                  <th className="text-left hidden md:table-cell">Service</th>
                  <th className="text-left">Status</th>
                  <th className="text-left hidden sm:table-cell">Date</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(stats.recent_inquiries) ? stats.recent_inquiries : []).map(inq => (
                  <tr key={inq.id}>
                    <td className="font-medium text-[#1F2937]">{inq.name}<br /><span className="text-xs text-[#9CA3AF] font-normal">{inq.email}</span></td>
                    <td className="hidden sm:table-cell text-[#6B7280]">{inq.company || '—'}</td>
                    <td className="hidden md:table-cell text-[#6B7280]">{inq.service || '—'}</td>
                    <td><span className={statusBadge(inq.status)}>{inq.status}</span></td>
                    <td className="hidden sm:table-cell text-[#9CA3AF]">{formatDate(inq.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
